import { useEffect, useState } from 'react';
import { WizardStepData, WizardStepType } from '@/components/wizard/types';

interface UseSubCategoryAutoSelectParams {
  categorySeeded: boolean;
  currentCategory: string;
  currentStepData: WizardStepData;
  /**
   * The category this auto-select applies to. The effect is inert for every
   * other category, which is what lets PreFormWizard run one instance per
   * derived sub-category without them interfering.
   */
  targetCategory: string;
  /**
   * The sub-category derived server-side from the owner (SubmissionPreFormBuilder
   * #owner_info) — `owner.eap_status` for New Site/Investigator,
   * `owner.continuing_review_sub_category` for Continuing Review.
   *
   * Undefined means "the backend could not determine it" and is NOT a reason to
   * pick a default: the step stays visible so the user answers it themselves.
   */
  derivedSubCategory?: string;
  /**
   * Whether a sub-category is currently selected. Back from a later step clears
   * the selection without changing the category, so this — not the category
   * alone — is what tells us a previously-seeded auto-select no longer applies.
   */
  hasSubCategorySelection: boolean;
  selectOption: (option: string) => void;
  // PreFormWizard passes true: category is user-editable there, so a pick away
  // from the target category (via Back/Reset or a fresh choice) must re-arm
  // the effect for a later re-pick. PreFormWizardWithSubCategory passes false:
  // its category is a fixed prop for the component's lifetime, so this branch
  // never fires in practice.
  reArmOnCategoryChange: boolean;
}

// Shared by PreFormWizard and PreFormWizardWithSubCategory: once the category
// is settled and the subcategory step's options are available, auto-select
// the subcategory the backend derived from the owner.
export const useSubCategoryAutoSelect = ({
  categorySeeded,
  currentCategory,
  currentStepData,
  targetCategory,
  derivedSubCategory,
  hasSubCategorySelection,
  selectOption,
  reArmOnCategoryChange,
}: UseSubCategoryAutoSelectParams) => {
  const [subCategorySeeded, setSubCategorySeeded] = useState(false);
  const [subCategoryAutoSelected, setSubCategoryAutoSelected] = useState(false);

  useEffect(() => {
    // Re-arm when this auto-select no longer applies, so returning to the step
    // later is evaluated afresh instead of staying seeded from the first visit.
    // Either the user moved to another category, or they went Back — which
    // clears the derived sub-category (previousStep) while leaving the category
    // alone. Without the second case, re-picking the same category after Back
    // shows the raw picker and the customer can choose a sub-category that
    // contradicts the owner, which is the whole thing this prevents.
    //
    // Deliberately keyed off the wizard's own state rather than
    // `subCategoryAutoSelected`: clearing that flag re-satisfies the seeding
    // branch below, which re-selects and sets it again, looping forever.
    const selectionWasCleared =
      currentStepData.currentStep !== WizardStepType.SUBCATEGORY &&
      !hasSubCategorySelection;

    if (
      reArmOnCategoryChange &&
      (currentCategory !== targetCategory || selectionWasCleared)
    ) {
      if (subCategorySeeded) setSubCategorySeeded(false);
      if (subCategoryAutoSelected) setSubCategoryAutoSelected(false);
      return;
    }

    if (
      categorySeeded &&
      !subCategorySeeded &&
      currentStepData.currentStep === WizardStepType.SUBCATEGORY &&
      currentStepData.options
    ) {
      setSubCategorySeeded(true);
      if (
        derivedSubCategory &&
        currentStepData.options.includes(derivedSubCategory)
      ) {
        setSubCategoryAutoSelected(true);
        selectOption(derivedSubCategory);
      }
      // If the derived value is missing, leave the subcategory step visible so
      // the user can answer it manually rather than blocking progress.
    }
  }, [
    reArmOnCategoryChange,
    currentCategory,
    targetCategory,
    hasSubCategorySelection,
    categorySeeded,
    subCategorySeeded,
    subCategoryAutoSelected,
    currentStepData.currentStep,
    currentStepData.options,
    derivedSubCategory,
    selectOption,
  ]);

  const resetSubCategorySeed = () => {
    setSubCategorySeeded(false);
    setSubCategoryAutoSelected(false);
  };

  return { subCategorySeeded, subCategoryAutoSelected, resetSubCategorySeed };
};
